import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
        const data = await response.json();
        
        if (response.ok && data.valid) {
          setTokenValid(true);
          setEmail(data.email);
        }
      } catch (error) {
        console.error('Token verification failed:', error);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Invalid or missing token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-14 w-auto object-contain"
              />
              <span className="font-heading text-2xl font-bold text-white uppercase">
                Score Copilot
              </span>
            </div>
          </div>

          <div className="bg-[#18181b] border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="text-red-500" size={32} />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-white uppercase mb-4">
              Invalid Reset Link
            </h1>
            
            <p className="text-gray-400 mb-6">
              This password reset link is invalid or has expired. 
              Please request a new reset link.
            </p>

            <div className="space-y-3">
              <Link to="/forgot-password">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                  Request New Link
                </Button>
              </Link>
              
              <Link to="/login">
                <Button variant="outline" className="w-full border-white/10 text-white hover:bg-white/5 mt-2">
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-14 w-auto object-contain"
              />
              <span className="font-heading text-2xl font-bold text-white uppercase">
                Score Copilot
              </span>
            </div>
          </div>

          <div className="bg-[#18181b] border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="text-emerald-500" size={32} />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-white uppercase mb-4">
              Password Reset!
            </h1>
            
            <p className="text-gray-400 mb-6">
              Your password has been successfully reset. 
              You can now log in with your new password.
            </p>

            <Link to="/login">
              <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/logo.jpg" 
              alt="Score Copilot" 
              className="h-14 w-auto object-contain"
            />
            <span className="font-heading text-2xl font-bold text-white uppercase">
              Score Copilot
            </span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-white uppercase">
            Reset Password
          </h1>
          <p className="text-gray-400 mt-2">
            Create a new password for <span className="text-emerald-400">{email}</span>
          </p>
        </div>

        <div className="bg-[#18181b] border border-white/10 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-gray-300">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-10 pr-10 bg-black/20 border-white/10 text-white placeholder:text-gray-500"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-10 bg-black/20 border-white/10 text-white placeholder:text-gray-500"
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>

            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-red-400 text-sm">Passwords do not match</p>
            )}

            <Button
              type="submit"
              disabled={loading || password !== confirmPassword}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 btn-lift disabled:opacity-50"
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
